/**
 * Bulls & Cows Cartridge Implementation
 */
import { BullsAndCowsEngine } from './engine.js';

export const Cartridge = {
  id: 'bulls-and-cows',
  name: 'Bulls & Cows',
  tagline: '4-digit numerical code deduction duel',
  icon: '🐂🐄',
  category: 'Logic',
  minPlayers: 1,
  maxPlayers: 2,
  modes: ['solo', 'multiplayer'],

  defaultConfig: {
    challengeType: 'practice'
  },

  configOptions: [
    {
      key: 'challengeType',
      label: 'Challenge Mode',
      type: 'select',
      options: [
        { value: 'practice', label: 'Practice (Random Secret)' },
        { value: 'daily', label: 'Daily Challenge (Universal Puzzle)' }
      ],
      soloOnly: true
    }
  ],

  async init(mountElement, context) {
    this.mountElement = mountElement;
    this.context = context;

    const challengeType = context.config?.challengeType || 'practice';
    this.engine = new BullsAndCowsEngine(challengeType);

    this.phase = context.mode === 'multiplayer' ? 'secret_setup' : 'guessing';
    this.currentInput = '';
    this.mySecret = '';
    this.opponentSecret = '';
    this.isMyTurn = context.isHost !== false;
    this.opponentGuessHistory = [];

    this.buildDOM();
    this.render();
  },

  buildDOM() {
    this.mountElement.innerHTML = `
      <div class="bc-wrapper" style="display:flex; flex-direction:column; align-items:center; width:100%; max-width:480px; margin:0 auto;">
        <div class="game-hud">
          <div class="hud-player">
            <span>🎯 ${this.context.mode === 'solo' ? (this.engine.mode === 'daily' ? 'Daily Puzzle' : 'Solo Practice') : 'You'}</span>
          </div>
          <div id="bc-turn-badge" class="hud-turn-indicator active">Enter Secret</div>
          <div class="hud-player">
            <span>${this.context.mode === 'solo' ? 'Guesses: 0' : this.context.opponentName || 'Opponent'}</span>
          </div>
        </div>

        <!-- Secret Setup Banner (Multiplayer only) -->
        <div id="bc-setup-banner" style="display:${this.phase === 'secret_setup' ? 'block' : 'none'}; width:100%; text-align:center; margin:10px 0; background:var(--bg-surface); padding:12px; border-radius:12px; border:1px solid var(--border-subtle);">
          <p style="font-weight:700; font-size:1rem; margin-bottom:4px;">🔒 Choose Your 4-Digit Secret Code</p>
          <p style="font-size:0.8rem; color:var(--text-muted);">Must have 4 unique digits. Leading zero (0) is allowed.</p>
        </div>

        <!-- 4-Digit Display Input Slots -->
        <div id="bc-digit-slots" style="display:flex; gap:10px; justify-content:center; margin:16px 0;">
          <div class="bc-slot" style="width:54px; height:64px; border:2px solid var(--accent-amber); border-radius:12px; background:var(--bg-surface); display:flex; align-items:center; justify-content:center; font-family:var(--font-mono); font-size:2rem; font-weight:800;"></div>
          <div class="bc-slot" style="width:54px; height:64px; border:2px solid var(--accent-amber); border-radius:12px; background:var(--bg-surface); display:flex; align-items:center; justify-content:center; font-family:var(--font-mono); font-size:2rem; font-weight:800;"></div>
          <div class="bc-slot" style="width:54px; height:64px; border:2px solid var(--accent-amber); border-radius:12px; background:var(--bg-surface); display:flex; align-items:center; justify-content:center; font-family:var(--font-mono); font-size:2rem; font-weight:800;"></div>
          <div class="bc-slot" style="width:54px; height:64px; border:2px solid var(--accent-amber); border-radius:12px; background:var(--bg-surface); display:flex; align-items:center; justify-content:center; font-family:var(--font-mono); font-size:2rem; font-weight:800;"></div>
        </div>

        <!-- Numeric Keypad -->
        <div id="bc-keypad" style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; width:100%; max-width:320px; margin-bottom:16px;">
          ${[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => `
            <button class="bc-num-btn btn btn-secondary" data-num="${n}" style="font-family:var(--font-mono); font-size:1.4rem; padding:12px 0;">${n}</button>
          `).join('')}
          <button id="bc-btn-clear" class="btn btn-secondary" style="font-size:1.1rem; padding:12px 0;">⌫</button>
          <button class="bc-num-btn btn btn-secondary" data-num="0" style="font-family:var(--font-mono); font-size:1.4rem; padding:12px 0;">0</button>
          <button id="bc-btn-submit" class="btn btn-primary" style="font-size:1.1rem; padding:12px 0;">↵</button>
        </div>

        <!-- Guess History Log -->
        <div style="width:100%; margin-top:8px;">
          <h4 style="font-size:0.85rem; color:var(--text-muted); text-transform:uppercase; margin-bottom:6px;">Guess History</h4>
          <div id="bc-history-list" style="display:flex; flex-direction:column-reverse; gap:6px; max-height:160px; overflow-y:auto; padding:4px;">
            <!-- History entries -->
          </div>
        </div>
      </div>
    `;

    this.turnBadge = this.mountElement.querySelector('#bc-turn-badge');
    this.slots = this.mountElement.querySelectorAll('.bc-slot');
    this.historyList = this.mountElement.querySelector('#bc-history-list');
    this.setupBanner = this.mountElement.querySelector('#bc-setup-banner');

    // Keypad listeners
    this.mountElement.querySelectorAll('.bc-num-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const num = btn.getAttribute('data-num');
        this.handleDigitPress(num);
      });
    });

    this.mountElement.querySelector('#bc-btn-clear').addEventListener('click', () => {
      this.handleBackspace();
    });

    this.mountElement.querySelector('#bc-btn-submit').addEventListener('click', () => {
      this.handleSubmit();
    });
  },

  handleDigitPress(digit) {
    if (this.currentInput.length >= 4) return;
    if (this.currentInput.includes(digit)) {
      this.context.audio?.playSfx('error');
      return;
    }

    this.currentInput += digit;
    this.context.audio?.playSfx('tap');
    this.updateSlots();
  },

  handleBackspace() {
    if (this.currentInput.length > 0) {
      this.currentInput = this.currentInput.slice(0, -1);
      this.context.audio?.playSfx('tap');
      this.updateSlots();
    }
  },

  handleSubmit() {
    if (this.currentInput.length !== 4) {
      this.context.audio?.playSfx('error');
      return;
    }

    if (this.phase === 'secret_setup') {
      // Player submitted their secret code for multiplayer
      this.mySecret = this.currentInput;
      this.currentInput = '';
      this.updateSlots();
      this.phase = 'waiting_opp_secret';
      this.setupBanner.innerHTML = `<p style="font-weight:700;">🔒 Secret Locked: ${this.mySecret}</p><p style="font-size:0.8rem; color:var(--text-muted);">Waiting for opponent to lock their secret code...</p>`;
      this.context.audio?.playSfx('success');
      this.context.sendAction('secret_ready', { ready: true });
      return;
    }

    if (this.phase === 'guessing') {
      if (this.context.mode === 'multiplayer' && !this.isMyTurn) {
        this.context.audio?.playSfx('error');
        return;
      }

      const guess = this.currentInput;
      this.currentInput = '';
      this.updateSlots();

      if (this.context.mode === 'solo') {
        const res = this.engine.evaluateGuess(guess);
        this.playFeedbackAudio(res.bulls, res.cows);
        this.renderHistory();

        if (res.win) {
          this.handleGameOver(true, `Brilliant deduction! You cracked code ${guess} in ${this.engine.guessHistory.length} attempts!`);
        }
      } else {
        // Send guess to opponent
        this.isMyTurn = false;
        this.updateHUD();
        this.context.sendAction('guess', { guess });
      }
    }
  },

  playFeedbackAudio(bulls, cows) {
    if (bulls > 0) {
      this.context.audio?.playSfx('bull');
    } else if (cows > 0) {
      this.context.audio?.playSfx('cow');
    } else {
      this.context.audio?.playSfx('miss');
    }
  },

  handleRemoteAction(action) {
    if (action.actionType === 'secret_ready') {
      this.opponentSecretReady = true;
      if (this.mySecret) {
        // Both players are ready!
        this.phase = 'guessing';
        this.setupBanner.style.display = 'none';
        this.updateHUD();
      }
    } else if (action.actionType === 'guess') {
      // Opponent made a guess on MY secret
      const { guess } = action.payload;
      const res = this.engine.evaluateGuess(guess, this.mySecret);

      // Reply with feedback
      this.context.sendAction('guess_result', {
        guess,
        bulls: res.bulls,
        cows: res.cows,
        win: res.win
      });

      if (res.win) {
        this.handleGameOver(false, `Opponent cracked your secret code ${this.mySecret}!`);
      } else {
        this.isMyTurn = true;
        this.updateHUD();
      }
    } else if (action.actionType === 'guess_result') {
      // Feedback on OUR guess on opponent's secret
      const { guess, bulls, cows, win } = action.payload;
      this.engine.guessHistory.push({ guess, bulls, cows, win, timestamp: Date.now() });
      this.playFeedbackAudio(bulls, cows);
      this.renderHistory();

      if (win) {
        this.handleGameOver(true, `Victory! You cracked the opponent's secret code in ${this.engine.guessHistory.length} attempts!`);
      }
    }
  },

  handleGameOver(isWin, reason) {
    this.phase = 'gameover';
    this.context.audio?.playSfx(isWin ? 'victory' : 'defeat');
    this.context.onGameOver?.({
      winnerId: isWin ? this.context.playerId : this.context.opponentId,
      isDraw: false,
      reason,
      stats: { attempts: this.engine.guessHistory.length }
    });
    this.updateHUD();
  },

  updateSlots() {
    this.slots.forEach((slot, i) => {
      const char = this.currentInput[i] || '';
      slot.textContent = char;
      slot.style.borderColor = char ? 'var(--accent-amber)' : 'var(--border-subtle)';
    });

    // Disable keys already typed
    this.mountElement.querySelectorAll('.bc-num-btn').forEach((btn) => {
      const num = btn.getAttribute('data-num');
      btn.disabled = this.currentInput.includes(num);
      btn.style.opacity = this.currentInput.includes(num) ? '0.35' : '1';
    });
  },

  renderHistory() {
    this.historyList.innerHTML = '';
    const list = [...this.engine.guessHistory].reverse();

    for (const item of list) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: var(--bg-surface);
        padding: 8px 12px;
        border-radius: var(--radius-sm);
        border: 1px solid var(--border-subtle);
      `;

      row.innerHTML = `
        <span style="font-family:var(--font-mono); font-size:1.1rem; font-weight:700; letter-spacing:0.1em;">
          ${item.guess}
        </span>
        <div style="display:flex; gap:10px; font-weight:700; font-size:0.85rem;">
          <span style="color:#f59e0b;">🎯 ${item.bulls} Bulls</span>
          <span style="color:#06b6d4;">🐮 ${item.cows} Cows</span>
        </div>
      `;
      this.historyList.appendChild(row);
    }
  },

  updateHUD() {
    if (!this.turnBadge) return;

    if (this.phase === 'secret_setup') {
      this.turnBadge.textContent = 'Lock In Secret';
      this.turnBadge.className = 'hud-turn-indicator active';
    } else if (this.phase === 'waiting_opp_secret') {
      this.turnBadge.textContent = 'Waiting for Opponent';
      this.turnBadge.className = 'hud-turn-indicator waiting';
    } else if (this.phase === 'gameover') {
      this.turnBadge.textContent = 'Match Finished';
      this.turnBadge.className = 'hud-turn-indicator waiting';
    } else {
      if (this.context.mode === 'solo' || this.isMyTurn) {
        this.turnBadge.textContent = 'Your Turn to Guess';
        this.turnBadge.className = 'hud-turn-indicator active';
      } else {
        this.turnBadge.textContent = 'Opponent Guessing...';
        this.turnBadge.className = 'hud-turn-indicator waiting';
      }
    }
  },

  render() {
    this.updateHUD();
    this.updateSlots();
    this.renderHistory();
  },

  serializeState() {
    return {
      engine: this.engine.serialize(),
      phase: this.phase,
      mySecret: this.mySecret,
      isMyTurn: this.isMyTurn
    };
  },

  restoreState(state) {
    if (state?.engine) {
      this.engine.restore(state.engine);
      this.phase = state.phase || 'guessing';
      this.mySecret = state.mySecret || '';
      this.isMyTurn = !!state.isMyTurn;
      this.render();
    }
  },

  pause() {},
  resume() {},
  destroy() {}
};
